import React, { createRef } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { useServerData } from '@xingternal/hops';
import { Headline } from '@xingternal/typography';

import { ShowMore } from '@/components/show-more';

import { SerpsRouteInfo, useSerpsMatch } from '../../hooks/use-serps-match';

import * as S from './seo-copies.styles';

const IdType = {
  H1: 'H_1',
  H2: 'H_2',
  H3: 'H_3',
  H4: 'H_4',
  H5: 'H_5',

  T1: 'TEXT_1',
  T2: 'TEXT_2',
  T3: 'TEXT_3',
  T4: 'TEXT_4',
  T5: 'TEXT_5',
} as const;

export type Keys = {
  H1?: string;
  H2?: string;
  H3?: string;
  H4?: string;

  T1?: string;
  T2?: string;
  T3?: string;
  T4?: string;
};

const getId = (value: string, type: (typeof IdType)[keyof typeof IdType]): string =>
  `XJM_SEO_FOOTER_TEXT_${value}_${type}`;

const getValue = ({ categoryValue: category, subcategoryValue: subcategory }: SerpsRouteInfo): string => {
  if (!category) {
    return '';
  }

  return subcategory ? `${category}_${subcategory}` : category;
};

const getKeys = (serps: SerpsRouteInfo, intl: IntlShape): Keys => {
  const value = getValue(serps).toUpperCase().replace(/-/g, '_');

  return {
    H1: intl.messages[getId(value, IdType.H1)] as string | undefined,
    H2: intl.messages[getId(value, IdType.H2)] as string | undefined,
    H3: intl.messages[getId(value, IdType.H3)] as string | undefined,
    H4: intl.messages[getId(value, IdType.H4)] as string | undefined,
    T1: intl.messages[getId(value, IdType.T1)] as string | undefined,
    T2: intl.messages[getId(value, IdType.T2)] as string | undefined,
    T3: intl.messages[getId(value, IdType.T3)] as string | undefined,
    T4: intl.messages[getId(value, IdType.T4)] as string | undefined,
  };
};

type TextRowProps = {
  header: string;
  text: string;
  innerRef?: React.RefObject<HTMLDivElement>;
};

const TextRow: React.FC<TextRowProps> = ({ header, text, innerRef }) => (
  <div ref={innerRef}>
    <Headline size="large">{header}</Headline>
    <S.Meta size="small">{text}</S.Meta>
  </div>
);

export const SeoCopies: React.VFC = () => {
  const serps = useSerpsMatch();
  const intl = useIntl();
  const { language } = useServerData();

  const containerRef = createRef<HTMLDivElement>();

  if (language !== 'de' || !serps) {
    return null;
  }

  const keys = getKeys(serps, intl);
  // If there's only the first header, then the others should not be displayed
  // nor the show more option
  const moreThanOne = !!keys.H2 || !!keys.H3 || !!keys.H4;

  // If there's no H1, there will not be any string at all
  if (!keys.H1 || !keys.T1) {
    return null;
  }

  return (
    <S.ColumnContainer size={12} data-cy="serps-seo-copies">
      {/* First row will never be hidden */}
      <TextRow header={keys.H1} text={keys.T1} innerRef={containerRef} />

      {moreThanOne && (
        <ShowMore
          textCollapsed={intl.formatMessage({ id: 'BUTTON_SHOW_MORE' })}
          textExpanded={intl.formatMessage({ id: 'BUTTON_SHOW_LESS' })}
          collapsedHeight={containerRef?.current?.getBoundingClientRect().height}
        >
          {!!keys.H2 && !!keys.T2 && <TextRow header={keys.H2} text={keys.T2} />}

          {!!keys.H3 && !!keys.T3 && <TextRow header={keys.H3} text={keys.T3} />}

          {!!keys.H4 && !!keys.T4 && <TextRow header={keys.H4} text={keys.T4} />}
        </ShowMore>
      )}
    </S.ColumnContainer>
  );
};
